import { useEffect, useState } from 'react';
import axios from 'axios';

/* Shared pieces for IVR Menus and Business Hours & Holidays:
   - useDestData(): lists a destination can point at (tenant scoped by the API)
   - DestPicker: destination type + value selector
   Voicemail is intentionally not offered until voicemail is enabled. */

export const DEST_TYPES: { v: string; l: string }[] = [
  { v: 'extension', l: 'Extension' },
  { v: 'queue', l: 'Queue' },
  { v: 'ivr', l: 'IVR menu' },
  { v: 'time_condition', l: 'Business hours rule' },
  { v: 'external', l: 'External number' },
  { v: 'announcement', l: 'Play greeting, then hang up' },
  { v: 'hangup', l: 'Hang up' },
];

/* Managing-site scope: a selected site limits extension destinations to its
   range; "All sites" (no site) shows every extension of the tenant. */
export function extInSite(ext: any, site?: any) {
  if (!site) return true;
  const s = String(ext ?? '');
  if (Array.isArray(site.members)) return site.members.includes(s); // 2026-10-30: site by assignment (TEC), not by number range
  return /^[0-9]+$/.test(s) && Number(s) >= Number(site.ext_start) && Number(s) <= Number(site.ext_end);
}

export function scopeToSite(data: any, site?: any) {
  if (!site) return data;
  return { ...data, extensions: (data.extensions || []).filter((x: any) => extInSite(x.extension_number, site)) };
}

export function useDestData(refreshKey?: any) {
  const [d, setD] = useState<any>({ extensions: [], queues: [], ivrs: [], tcs: [], prompts: [] });
  useEffect(() => {
    const get = (u: string) => axios.get(u).then((r) => r.data).catch(() => []);
    Promise.all([get('/api/extensions'), get('/api/queues'), get('/api/asterisk/ivr'), get('/api/asterisk/time-conditions'), get('/api/ivr-prompts')])
      .then(([extensions, queues, ivrs, tcs, prompts]) => setD({
        extensions: Array.isArray(extensions) ? extensions : [], queues: Array.isArray(queues) ? queues : [],
        ivrs: Array.isArray(ivrs) ? ivrs : [], tcs: Array.isArray(tcs) ? tcs : [], prompts: Array.isArray(prompts) ? prompts : [],
      }));
  }, [refreshKey]);
  return d;
}

export function destLabel(type: string, value: string, data: any) {
  const t = DEST_TYPES.find((x) => x.v === type);
  if (!type || type === 'hangup') return 'Hang up';
  if (type === 'announcement') { const p = data.prompts.find((x: any) => x.sound === value); return 'Greeting: ' + (p ? p.name : value); }
  if (type === 'ivr') { const m = data.ivrs.find((x: any) => x.menu_name === value); return 'IVR: ' + (m ? (m.display_name || m.menu_name) : value); }
  if (type === 'time_condition') { const m = data.tcs.find((x: any) => x.condition_name === value); return 'Hours: ' + (m ? (m.display_name || m.condition_name) : value); }
  return (t ? t.l : type) + (value ? ': ' + value : '');
}

export function DestPicker({ type, value, onChange, data, excludeIvr, excludeTc, required }: {
  type: string; value: string; onChange: (type: string, value: string) => void; data: any;
  excludeIvr?: string; excludeTc?: string; required?: boolean;
}) {
  const sel = { padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 6, minWidth: 0 } as any;
  let valueControl: any = null;
  if (type === 'extension') valueControl = (
    <select style={sel} value={value || ''} onChange={(e) => onChange(type, e.target.value)}>
      <option value="">{data.extensions.length ? 'Select extension…' : 'No extensions in this site'}</option>
      {value && !data.extensions.some((x: any) => String(x.extension_number) === String(value)) && <option value={value}>{value}</option>}
      {data.extensions.map((x: any) => <option key={x.id} value={x.extension_number}>{x.extension_number} – {x.display_name || x.username || ''}</option>)}
    </select>);
  else if (type === 'queue') valueControl = (
    <select style={sel} value={value || ''} onChange={(e) => onChange(type, e.target.value)}>
      <option value="">Select queue…</option>
      {data.queues.map((q: any) => <option key={q.id} value={q.queue_name}>{q.display_name || q.queue_name}</option>)}
    </select>);
  else if (type === 'ivr') valueControl = (
    <select style={sel} value={value || ''} onChange={(e) => onChange(type, e.target.value)}>
      <option value="">Select IVR menu…</option>
      {data.ivrs.filter((m: any) => m.menu_name !== excludeIvr).map((m: any) => <option key={m.id} value={m.menu_name}>{m.display_name || m.menu_name}</option>)}
    </select>);
  else if (type === 'time_condition') valueControl = (
    <select style={sel} value={value || ''} onChange={(e) => onChange(type, e.target.value)}>
      <option value="">Select hours rule…</option>
      {data.tcs.filter((m: any) => m.condition_name !== excludeTc).map((m: any) => <option key={m.id} value={m.condition_name}>{m.display_name || m.condition_name}</option>)}
    </select>);
  else if (type === 'external') valueControl = (
    <input style={sel} value={value || ''} placeholder="e.g. 0501234567 or +971…" onChange={(e) => onChange(type, e.target.value.replace(/[^0-9+]/g, ''))} />);
  else if (type === 'announcement') valueControl = (
    <select style={sel} value={value || ''} onChange={(e) => onChange(type, e.target.value)}>
      <option value="">Select greeting…</option>
      {data.prompts.map((p: any) => <option key={p.id} value={p.sound}>{p.name}</option>)}
    </select>);
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <select style={sel} value={type || (required ? '' : 'hangup')} onChange={(e) => onChange(e.target.value, '')}>
        {required && <option value="">Choose destination…</option>}
        {DEST_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
      </select>
      {valueControl}
    </div>
  );
}

export function destValid(type: string, value: string) {
  if (!type) return false;
  if (type === 'hangup') return true;
  return !!String(value || '').trim();
}
